import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["loader", "LoadingComponent"],
  _excluded2 = ["loader", "LoadingComponent", "componentProps"];
import { Loadable } from 'foundations-components/transitional/utils/Loadable';
import { useEffect, useMemo } from 'react';
import ErrorPage from '../../pages/ErrorPage';
import { jsx as _jsx } from "react/jsx-runtime";
const CHUNK_RELOAD_KEY = 'pricing-pages-chunk-reload';

// After a deploy, browsers with the old HTML try to fetch chunks that no
// longer exist (the hashes changed). A single reload fetches the new HTML
// with correct chunk URLs. sessionStorage prevents infinite reload loops.
function ChunkLoadErrorFallback({
  error
}) {
  const isChunkError = error.name === 'ChunkLoadError' || /loading chunk .* failed/i.test(error.message) || /loadable.*split chunk/i.test(error.message);
  const hasReloaded = (() => {
    try {
      return !!sessionStorage.getItem(CHUNK_RELOAD_KEY);
    } catch (_unused) {
      return false;
    }
  })();
  useEffect(() => {
    if (isChunkError && !hasReloaded) {
      try {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
      } catch (_unused2) {
        return;
      }
      window.location.reload();
    }
  }, [isChunkError, hasReloaded]);
  if (isChunkError && !hasReloaded) {
    return null;
  }
  if (isChunkError) {
    // Reload already happened and still failing. Clear flag so future visits can retry.
    try {
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    } catch (_unused3) {
      // ignore
    }
  }
  return /*#__PURE__*/_jsx(ErrorPage, {});
}
export const makeLoadable = _ref => {
  let {
      loader,
      LoadingComponent
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return Loadable(Object.assign({
    loader,
    LoadingComponent,
    ErrorComponent: ChunkLoadErrorFallback
  }, rest));
};

/**
 * A wrapper for Loadable that will pass common props from a common prop hook to both the loading and loaded component
 *
 * @param loader The loader for the full component
 * @param LoadingComponent The component for the loading state
 * @param componentProps Props to be passed to the loading and loaded component for both the loading and loaded component.
 * @param rest The rest of the props to pass to Loadable
 */
export const LoadableWrapper = _ref2 => {
  let {
      loader,
      LoadingComponent,
      componentProps
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const ComponentLoadable = useMemo(() => makeLoadable(Object.assign({
    loader,
    LoadingComponent: () => LoadingComponent ? /*#__PURE__*/_jsx(LoadingComponent, Object.assign({}, componentProps || {})) : null
  }, rest)),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);

  // eslint-disable-next-line react-hooks/static-components
  return /*#__PURE__*/_jsx(ComponentLoadable, Object.assign({}, componentProps));
};
export default LoadableWrapper;